import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { PublicKey } from '@solana/web3.js';
import { getSolBalance, getTokenPrice } from '../utils/getBalances';

const DEBOUNCE_DELAY = 1000; // 1 секунда

interface WalletContextType {
  publicKey: PublicKey | null;
  setPublicKey: (key: PublicKey | null) => void;
  balance: number;
  setBalance: (balance: number) => void;
}

const WalletContext = createContext<WalletContextType>({
  publicKey: null,
  setPublicKey: () => {},
  balance: 0,
  setBalance: () => {},
});

export const useWallet = () => useContext(WalletContext);

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [publicKey, setPublicKey] = useState<PublicKey | null>(null);
  const [balance, setBalance] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingRef = useRef(false);

  const updateBalance = useCallback(async (publicKey: PublicKey) => {
    // Если уже идет обновление, не начинаем новое
    if (isUpdatingRef.current) {
      return;
    }

    // Очищаем предыдущий таймаут
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Устанавливаем новый таймаут
    timeoutRef.current = setTimeout(async () => {
      try {
        isUpdatingRef.current = true;
        const solBalance = await getSolBalance(publicKey.toString());
        if (solBalance !== null) {
          const prices = await getTokenPrice("solana");
          const solPrice = prices.find((price: { symbol: string; price: number }) => price.symbol === "solana")?.price || 0;
          setBalance(solBalance * solPrice);
        }
      } catch (error) {
        console.error('Error fetching wallet balance:', error);
        setBalance(0);
      } finally {
        isUpdatingRef.current = false;
      }
    }, DEBOUNCE_DELAY);
  }, []);

  // Очищаем таймаут при размонтировании
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Обновляем баланс при изменении publicKey
  useEffect(() => {
    if (publicKey) {
      updateBalance(publicKey);
    } else {
      setBalance(0);
    }
  }, [publicKey, updateBalance]);

  return (
    <WalletContext.Provider value={{ publicKey, setPublicKey, balance, setBalance }}>
      {children}
    </WalletContext.Provider>
  );
}; 